import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { formatNumber } from "../../utils/formatNumber";
import AlertModal from "../AlertModal";
import { useAccount, useSecretNetworkClient } from "../../models/account";
import {
  cancelP2PDeal,
  disputeDeal,
  enterP2PDeal,
  P2PConfig,
  P2PDeal,
  P2PPost,
  resolveDeal,
} from "../../models/p2p";
import PostCard from "./PostCard";
import useIsMobile from "../../utils/useIsMobile";
import BankTransferModal from "./BankTransferModal";

interface Props {
  open: boolean;
  onClose: () => void;
  post?: P2PPost;
  config?: P2PConfig;
  refetch?: () => void;

  onButtonClick?: () => void;
  buttonText?: string;
  buttonBadge?: string | number;
  buttonDisabledText?: string;
  onAltButtonClick?: () => void;
  altButtonText?: string;
  altButtonDisabledText?: string;
}

const MyDealsModal: FC<Props> = ({
  open,
  onClose,
  post,
  config,
  refetch,
  ...props
}) => {
  const secretjs = useSecretNetworkClient();
  const { account } = useAccount();
  const isMobile = useIsMobile();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [transferModalDeal, setTransferModalDeal] = useState<P2PDeal>();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (refetch && (success || error)) {
      refetch();
    }
  }, [refetch, success, error]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>My Deals</DialogTitle>
        <DialogContent>
          <PostCard
            config={config}
            post={post}
            isMobile={isMobile}
            {...props}
          />
          {post?.dealer === account.address && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Trader</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(post?.deals || []).map((deal) => (
                    <TableRow key={deal.deal_id}>
                      <TableCell>{deal.deal_id}</TableCell>
                      <TableCell>
                        {deal.customer.slice(0, 8)}...{deal.customer.slice(-8)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(
                          deal.amount / 10 ** (config?.decimals || 6)
                        )}{" "}
                        {config?.name || ""}
                      </TableCell>
                      <TableCell
                        sx={(theme) => ({
                          textTransform: "capitalize",
                          color:
                            deal.state === "dispute"
                              ? theme.palette.error.main
                              : theme.palette.text.primary,
                        })}
                      >
                        {deal.state.replace("_bank", "").replace(/_/g, " ")}
                      </TableCell>
                      <TableCell align="right">
                        {(deal.state === "pend_customer_deposit" ||
                          deal.state === "pend_customer_transfer") && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={loading}
                            onClick={async () => {
                              try {
                                setLoading(true);
                                await cancelP2PDeal(
                                  {
                                    contract_address:
                                      config?.contract_address || "",
                                    code_hash: config?.code_hash || "",
                                    address: account.address,
                                    deal_id: deal?.deal_id || "",
                                  },
                                  secretjs!
                                );
                                setSuccess(
                                  "You have successfully canceled your trade!"
                                );
                                setLoading(false);
                                onClose();
                              } catch (err: any) {
                                setLoading(false);
                                setError(err.message);
                              }
                            }}
                          >
                            {loading ? (
                              <CircularProgress sx={{ mx: 1.5 }} size={25} />
                            ) : (
                              "Cancel"
                            )}
                          </Button>
                        )}
                        {(deal.state === "pend_dealer_bank_transfer" ||
                          deal.state === "pend_customer_sign_off") && (
                          <Button
                            sx={{ ml: 2 }}
                            size="small"
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                              setTransferModalDeal(deal);
                            }}
                          >
                            {loading ? (
                              <CircularProgress sx={{ mx: 1.5 }} size={25} />
                            ) : (
                              "Payment Detail"
                            )}
                          </Button>
                        )}
                        {deal.state === "pend_dealer_sign_off" && (
                          <>
                            <Button
                              sx={{ ml: 2 }}
                              size="small"
                              variant="contained"
                              disabled={loading}
                              onClick={async () => {
                                try {
                                  setLoading(true);
                                  await disputeDeal(
                                    {
                                      contract_address:
                                        config?.contract_address || "",
                                      code_hash: config?.code_hash || "",
                                      address: account.address,
                                      deal_id: deal?.deal_id || "",
                                    },
                                    secretjs!
                                  );
                                  setSuccess(
                                    `You have raised a dispute to the trade. Please create a ticket <a href="https://discord.com/channels/952397096722505768/1322233983391498324" target="_blank">here</a> to contact moderator.`
                                  );
                                  setLoading(false);
                                  onClose();
                                } catch (err: any) {
                                  setError(err.message);
                                  setLoading(false);
                                }
                              }}
                            >
                              {loading ? (
                                <CircularProgress sx={{ mx: 1.5 }} size={25} />
                              ) : (
                                "Dispute"
                              )}
                            </Button>
                            <Button
                              sx={{ ml: 2 }}
                              size="small"
                              variant="contained"
                              disabled={loading}
                              onClick={async () => {
                                try {
                                  setLoading(true);
                                  await resolveDeal(
                                    {
                                      contract_address:
                                        config?.contract_address || "",
                                      code_hash: config?.code_hash || "",
                                      address: account.address,
                                      deal_id: deal?.deal_id || "",
                                    },
                                    secretjs!
                                  );
                                  setLoading(false);
                                  setSuccess(
                                    "You have successfully signed off the trade!"
                                  );
                                  onClose();
                                } catch (err: any) {
                                  setLoading(false);
                                  setError(err.message);
                                }
                              }}
                            >
                              {loading ? (
                                <CircularProgress sx={{ mx: 1.5 }} size={25} />
                              ) : (
                                "Sign Off"
                              )}
                            </Button>
                          </>
                        )}
                        {deal.state === "dispute" && (
                          <Link
                            href="https://discord.com/channels/952397096722505768/1322233983391498324"
                            target="_blank"
                          >
                            Create Ticket
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!post?.deals?.length && (
            <Typography
              mt={4}
              variant="h5"
              align="center"
              color="textSecondary"
            >
              No Open Trades
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button sx={{ mr: 1 }} variant="text" size="large" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <AlertModal
        open={!!success}
        onClose={() => setSuccess("")}
        title="Success"
        html={success}
        type="success"
      />
      <AlertModal
        open={!!error}
        onClose={() => setError("")}
        title="Error"
        description={error}
        type="error"
      />
      <BankTransferModal
        open={!!transferModalDeal}
        onClose={() => setTransferModalDeal(undefined)}
        deal={transferModalDeal}
        config={config}
        refetch={refetch}
      />
    </>
  );
};

export default MyDealsModal;
